import { isPro } from "../utils/features";

interface RecalcSnapshot {
  requiredIntake: number | null;
  requiredNetPerDay: number | null;
  tdee: number | null;
}

interface DailyTargetsCardProps {
  /** Static intake target from the sprint plan (targetIntakeForForecast) */
  targetIntake: number | null;
  /** Static net calorie target from the sprint plan (effectiveNetCaloriesForForecast) */
  targetNet: number | null;
  /** Dynamic recalibration data for Pro adjustment (subset of recalcData) */
  recalcData: RecalcSnapshot | null;
  /** User gender for intake floor guardrail ("M" | "F" | null) */
  gender: string | null;
  /** Plan intensity label e.g. "Lean cut", "Moderate bulk", "Maintain" */
  modeLabel: string | null;
  /** Chip tone matching existing sprint category chip colors */
  modeTone: "blue" | "orange" | "purple" | null;
}

// Matches existing Pill tone backgrounds from WeightTracker.tsx exactly
const TONE_BG: Record<string, string> = {
  blue:   "rgba(59,130,246,0.20)",
  orange: "rgba(245,158,11,0.14)",
  purple: "rgba(139,92,246,0.20)",
};

function cardStyle(belowFloor: boolean) {
  return {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderTop: belowFloor ? "1.5px solid rgba(245,158,11,0.55)" : "1.5px solid rgba(59,130,246,0.45)",
    borderRadius: 18,
    padding: "1.2rem",
    backdropFilter: "blur(10px)",
    height: "100%",
    boxSizing: "border-box" as const,
    position: "relative" as const,
  };
}

const HEADER_STYLE = {
  fontSize: "1.1rem",
  fontWeight: 900,
  color: "#FFFFFF",
  minWidth: 0,
};

const NUMBER_STYLE = {
  fontSize: "2rem",
  fontWeight: 900,
  color: "#FFFFFF",
  lineHeight: 1.05,
  letterSpacing: "-0.02em",
} as const;

// Net is secondary emphasis — same size, softer color
const NET_NUMBER_STYLE = {
  ...NUMBER_STYLE,
  color: "rgba(255,255,255,0.7)",
} as const;

const LABEL_STYLE = {
  fontSize: "0.72rem",
  color: "rgba(255,255,255,0.7)",
  fontWeight: 600,
  lineHeight: 1.2,
} as const;

const UNIT_STYLE = {
  fontSize: "0.72rem",
  color: "rgba(255,255,255,0.45)",
  fontWeight: 600,
} as const;

const AUTO_BADGE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  padding: "0.16rem 0.48rem",
  borderRadius: 999,
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "rgba(255,255,255,0.92)",
  fontSize: "0.72rem",
  fontWeight: 700,
  lineHeight: 1.15,
} as const;

const MAX_DEFICIT = 1000;
const MAX_DAY_SWING = 200;

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function formatCal(val: number): string {
  return Math.round(val).toLocaleString();
}

function formatNetCal(val: number): string {
  const roundedRaw = Math.round(val);
  const rounded = Object.is(roundedRaw, -0) ? 0 : roundedRaw;
  if (rounded === 0) return "0";
  return rounded >= 0 ? `+${rounded.toLocaleString()}` : rounded.toLocaleString();
}

export function DailyTargetsCard({
  targetIntake, targetNet, recalcData, gender, modeLabel, modeTone,
}: DailyTargetsCardProps) {
  if (targetIntake === null || targetNet === null) return null;

  const pro = isPro();

  const intakeFloor = String(gender ?? "").trim().toUpperCase() === "F" ? 1200 : 1500;
  const belowFloor = targetIntake < intakeFloor;

  // Display the user's chosen target as-is. The calorie floor is surfaced as
  // a soft warning (amber border + "Low intake" badge below) rather than a
  // silent override — the pace clamp is the hard safety mechanism.
  let displayIntake = targetIntake;
  let displayNet = targetNet;
  let activityDelta: number | null = null;

  if (pro && recalcData?.requiredIntake !== null && recalcData?.requiredNetPerDay !== null) {
    const rawIntake = recalcData.requiredIntake ?? targetIntake;
    const rawNet = recalcData.requiredNetPerDay ?? targetNet;

    // Cap day-over-day swing vs static plan target
    const swing = clamp(rawIntake - targetIntake, -MAX_DAY_SWING, MAX_DAY_SWING);
    const swungIntake = targetIntake + swing;

    // Apply guardrails: max deficit (the pace clamp expressed as cal/day)
    const guardedNet = clamp(rawNet, -MAX_DEFICIT, MAX_DEFICIT);
    const guardedIntake = swungIntake;

    activityDelta = Math.round(guardedIntake - targetIntake);
    displayIntake = guardedIntake;
    displayNet = guardedNet;
  }

  const deltaStr = activityDelta !== null
    ? `${activityDelta >= 0 ? "+" : ""}${activityDelta.toLocaleString()} cal today · activity`
    : null;

  return (
    <div style={cardStyle(belowFloor)}>
      {/* Inner flex column — vertically centers content when card is height-stretched */}
      <div style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "center" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ ...HEADER_STYLE, flex: "1 1 auto", minWidth: 0 }}>Today's target</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", flexWrap: "wrap" }}>
            {modeLabel && modeTone && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "0.16rem 0.48rem",
                borderRadius: 999,
                background: TONE_BG[modeTone] ?? "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.92)",
                fontSize: "0.72rem",
                fontWeight: 700,
                lineHeight: 1.15,
              }}>
                {modeLabel}
              </span>
            )}
            {pro && <span style={AUTO_BADGE_STYLE}>Auto</span>}
          </div>
        </div>

        {/* Centered 2-column numbers with vertical divider + blue radial glow */}
        <div style={{
          display: "flex",
          alignItems: "stretch",
          minHeight: "4.5rem",
          marginTop: "0.75rem",
          background: "radial-gradient(ellipse at center, rgba(59,130,246,0.09) 0%, transparent 70%)",
          borderRadius: 10,
        }}>
          {/* Intake column — primary emphasis */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.25rem" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.2rem" }}>
              <span style={NUMBER_STYLE}>{formatCal(displayIntake)}</span>
              <span style={UNIT_STYLE}>cal</span>
            </div>
            <span style={LABEL_STYLE}>Intake</span>
          </div>

          {/* Vertical divider — spans ~60% of column height, vertically centered */}
          <div style={{ position: "relative", flexShrink: 0, width: 1, margin: "0 0.75rem" }}>
            <div style={{ position: "absolute", top: "20%", bottom: "20%", width: 1, background: "rgba(255,255,255,0.08)" }} />
          </div>

          {/* Net column — secondary emphasis */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.25rem" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.2rem" }}>
              <span style={NET_NUMBER_STYLE}>{formatNetCal(displayNet)}</span>
              <span style={UNIT_STYLE}>cal</span>
            </div>
            <span style={LABEL_STYLE}>Net</span>
          </div>
        </div>

        {/* Activity delta line (Pro only) */}
        {deltaStr && (
          <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.55)", marginTop: "0.5rem", fontWeight: 600, textAlign: "center" }}>
            {deltaStr}
          </div>
        )}
        {/* Helper text */}
        <div style={{ marginTop: "0.75rem", fontSize: "0.68rem", color: "rgba(255,255,255,0.45)", textAlign: "center" }}>
          {belowFloor ? (
            <span>
              <span style={{ color: "rgba(251,191,36,0.88)", fontWeight: 700 }}>Low intake</span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}> • </span>
              <span>Adjusts with your activity</span>
            </span>
          ) : (
            <span>Adjusts with your activity</span>
          )}
        </div>

        {/* TODO: render upsell link when payments wired */}
      </div>
    </div>
  );
}
